/* Reine Logik der Aufgabenliste (AUFGABEN-LISTE.md): Frist je Zeile, Sortieren, Ressortfarben, Texte beim Löschen.
   Ohne React, mit Vitest getestet. */
import { plusTage, tagMitWochentag } from "../../kern/kalender";
import { heute } from "../../kern/zeit";
import type { Aufgabe, Prioritaet } from "./logik";

export interface FristAnzeige {
  /** „Heute“, „Morgen“, „Mo. 26.10.“, ohne Datum „Sprintende“ (im Sprint) oder „Frist“ (Backlog) */
  text: string;
  /** Kein eigenes Datum: grau */
  leer: boolean;
  /** Überfällig: Datum in #C92F1A, darunter das Wort „überfällig“ */
  ueberfaellig: boolean;
  /** Erledigt: grau */
  fertig: boolean;
  /** „Frist: Mo. 12.10., überfällig“ oder „Frist: keine, es gilt das Sprintende Mo. 26.10.“ */
  vorlesen: string;
}

type FristFelder = Pick<Aufgabe, "frist_datum" | "frist_gilt" | "status" | "sprint_id">;

/** Überfällig nach der Regel vom 12.10.: geltende Frist vor heute (Berliner Zeit), Erledigtes nie. Wie die View aufgaben_liste. */
export function istUeberfaellig(a: Pick<Aufgabe, "frist_gilt" | "status">, heuteTag: string = heute()): boolean {
  return a.status !== "erledigt" && !!a.frist_gilt && a.frist_gilt < heuteTag;
}

/** Frist im Knopf einer Zeile (Abschnitt 3.2). */
export function fristAnzeige(a: FristFelder, heuteTag: string = heute()): FristAnzeige {
  const ueberfaellig = istUeberfaellig(a, heuteTag);
  const fertig = a.status === "erledigt";
  if (a.frist_datum) {
    const d = a.frist_datum;
    const text = d === heuteTag ? "Heute" : d === plusTage(heuteTag, 1) ? "Morgen" : tagMitWochentag(d);
    return { text, leer: false, ueberfaellig, fertig, vorlesen: `Frist: ${tagMitWochentag(d)}${ueberfaellig ? ", überfällig" : ""}` };
  }
  if (a.sprint_id) {
    const gilt = a.frist_gilt ? ` ${tagMitWochentag(a.frist_gilt)}` : "";
    return { text: "Sprintende", leer: true, ueberfaellig, fertig, vorlesen: `Frist: keine, es gilt das Sprintende${gilt}${ueberfaellig ? ", überfällig" : ""}` };
  }
  return { text: "Frist", leer: true, ueberfaellig: false, fertig, vorlesen: "Frist: keine" };
}

export type SortFeld = "frist" | "prioritaet";
export interface Sortierung {
  feld: SortFeld;
  richtung: "auf" | "ab";
}

/** Klick auf eine Spalte: aufsteigend, absteigend, aus. Eine andere Spalte beginnt aufsteigend. */
export function naechsteSortierung(jetzt: Sortierung | null, feld: SortFeld): Sortierung | null {
  if (!jetzt || jetzt.feld !== feld) return { feld, richtung: "auf" };
  return jetzt.richtung === "auf" ? { feld, richtung: "ab" } : null;
}

const PRIO_RANG: Record<Prioritaet, number> = { hoch: 0, normal: 1, niedrig: 2 };

/** Sortieren nach geltender Frist (ohne Frist immer zuletzt) oder Priorität (aufsteigend: Hoch zuerst). Sonst bleibt die Reihenfolge. */
export function sortiereListe<T extends Pick<Aufgabe, "frist_gilt" | "prioritaet">>(liste: readonly T[], sort: Sortierung | null): T[] {
  if (!sort) return [...liste];
  const faktor = sort.richtung === "auf" ? 1 : -1;
  return liste
    .map((a, i) => ({ a, i }))
    .sort((x, y) => {
      if (sort.feld === "frist") {
        const fx = x.a.frist_gilt;
        const fy = y.a.frist_gilt;
        if (!fx || !fy) return Number(!fx) - Number(!fy) || x.i - y.i;
        return faktor * fx.localeCompare(fy) || x.i - y.i;
      }
      return faktor * (PRIO_RANG[x.a.prioritaet] - PRIO_RANG[y.a.prioritaet]) || x.i - y.i;
    })
    .map((x) => x.a);
}

/** Sieben feste Farben (helle Fläche, dunkle Schrift, Kontrast ab 4,5:1), klassen r0 bis r6 in liste.module.css. */
export const RESSORT_FARBEN = 7;
/** Flächen der Ressortfarben, wie .r0 bis .r6 in liste.module.css. Der Punkt vor einer Kapsel in der Seitenleiste nimmt sie. */
export const RESSORT_FLAECHEN: readonly string[] = ["#f3e7cf", "#ddefe4", "#e3e7ee", "#e9e3f5", "#fce3dc", "#fcefc4", "#dceef5"];

/** Farbe je Ressort nach Reihenfolge des Anlegens, auch inaktive zählen mit, damit eine Farbe nicht wandert.
 *  Neue Ressorts bekommen die nächste Farbe. */
export function ressortFarben(ressorts: readonly { id: string; erstellt_am?: string | null }[]): Map<string, number> {
  const sortiert = [...ressorts].sort((a, b) => (a.erstellt_am ?? "").localeCompare(b.erstellt_am ?? "") || a.id.localeCompare(b.id));
  return new Map(sortiert.map((r, i) => [r.id, i % RESSORT_FARBEN]));
}

/** Rückfrage vor dem Löschen (Abschnitt 4). Die Teile mit Unteraufgaben und Kommentaren nur, wenn es welche gibt. */
export function loeschFrage(a: Pick<Aufgabe, "titel" | "eltern_id" | "eltern_titel">, unteraufgaben: number, kommentare: number): { titel: string; text: string } {
  const teile: string[] = [];
  if (unteraufgaben) teile.push(unteraufgaben === 1 ? "einer Unteraufgabe" : `${unteraufgaben} Unteraufgaben`);
  if (kommentare) teile.push(kommentare === 1 ? "einem Kommentar" : `${kommentare} Kommentaren`);
  const wo = a.eltern_id ? `aus der Aufgabe „${a.eltern_titel ?? "…"}“` : "aus allen Listen";
  return {
    titel: a.eltern_id ? "Unteraufgabe löschen?" : "Aufgabe löschen?",
    text: `„${a.titel}“ verschwindet ${wo}${teile.length ? `, mit ${teile.join(" und ")}` : ""}. Unter „Gelöscht“ kannst du sie wiederherstellen.`,
  };
}

/** Zeichen hinter dem Titel (BAUPLAN-AUFGABENLISTE.md, Abschnitt 4): kurz „1/2“ und „2“, für Screenreader ausgeschrieben. Ohne
 *  Unteraufgaben oder Kommentare fällt das Zeichen weg. */
export function titelZeichen(unterErledigt: number, unterGesamt: number, kommentare: number) {
  return {
    unter: unterGesamt ? { kurz: `${unterErledigt}/${unterGesamt}`, lang: `${unterErledigt} von ${unterGesamt} Unteraufgaben erledigt` } : null,
    kommentare: kommentare ? { kurz: String(kommentare), lang: kommentare === 1 ? "1 Kommentar" : `${kommentare} Kommentare` } : null,
  };
}

/** „Di. 13.10. bis Mo. 26.10.“ im Kopf des Sprints */
export function zeitraumMitTag(s: { beginn: string; ende: string }): string {
  return `${tagMitWochentag(s.beginn)} bis ${tagMitWochentag(s.ende)}`;
}

/** Tag eines Zeitpunkts in Berlin als „13.10.“ (gelöscht am) */
export function tagVon(iso: string): string {
  const t = heute(new Date(iso));
  return `${t.slice(8, 10)}.${t.slice(5, 7)}.`;
}

/** Bis wann sich eine gelöschte Aufgabe ohne Vorhaben wiederherstellen lässt: 12 Monate nach dem Löschen (Paket F, ADR-017).
 *  Wie in der Datenbank wird aus dem 29.02. im Folgejahr der 28.02. */
export function wiederherstellenBis(iso: string): string {
  const t = heute(new Date(iso));
  const tag = t.slice(5) === "02-29" ? "28.02." : `${t.slice(8, 10)}.${t.slice(5, 7)}.`;
  return `${tag}${Number(t.slice(0, 4)) + 1}`;
}
